import { X } from 'lucide-react';
import { Button } from '../../primitives/button.js';
import { resolveConfirm } from '../confirm.js';
import type { BulkAction, ConfirmSpec } from '../types.js';

export interface BulkBarProps<TItem> {
  rows: readonly TItem[];
  actions: readonly BulkAction<TItem>[];
  onClear: () => void;
  onConfirm: (
    spec: ConfirmSpec,
    tone: 'default' | 'danger',
    run: () => void | Promise<void>,
  ) => void;
}

/**
 * Replaces the toolbar while a selection exists rather than floating over the
 * table: on a dense list a floating bar covers the rows the user is choosing.
 */
export function BulkBar<TItem>({ rows, actions, onClear, onConfirm }: BulkBarProps<TItem>) {
  return (
    <div
      data-slot="bulk-bar"
      className="flex h-[var(--control-height)] items-center gap-2 rounded-control bg-accent-soft px-2 text-accent-soft-ink"
    >
      <Button variant="ghost" size="icon-sm" aria-label="Annulla selezione" onClick={onClear}>
        <X />
      </Button>
      <span className="text-secondary font-medium">
        {rows.length === 1 ? '1 selezionato' : `${String(rows.length)} selezionati`}
      </span>
      <div className="ml-auto flex items-center gap-1">
        {actions.map((action) => (
          <Button
            key={action.id}
            size="sm"
            variant={action.tone === 'danger' ? 'danger' : 'ghost'}
            onClick={() => {
              const spec = resolveConfirm(action.confirm, rows);
              const run = () => action.onSelect(rows);
              if (spec) onConfirm(spec, action.tone ?? 'default', run);
              else void run();
            }}
          >
            {action.icon}
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
